import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLUGS = [
  { slug: 'terms',   label: 'Terms of Service',  hint: 'Linked from the registration form (required acceptance).' },
  { slug: 'privacy', label: 'Privacy Policy',    hint: 'GDPR-style notice describing how customer data is processed.' },
  { slug: 'license', label: 'Licence Agreement', hint: 'Linked from the registration form (required acceptance).' },
  { slug: 'refund',  label: 'Refund Policy',     hint: 'Linked from the registration form (required acceptance).' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'clean'],
  ],
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align', 'link',
];

/**
 * Admin editor for the four legal documents. Each is stored as HTML in
 * `app_config` (key `legal:{slug}`) and rendered on the public pages
 * /terms /privacy /license /refund-policy.
 */
export default function LegalDocsEditor({ token }) {
  const [active, setActive] = useState('terms');
  const [docs, setDocs] = useState({});      // {slug: html}
  const [meta, setMeta] = useState({});      // {slug: {has_content, updated_at}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetch(`${API_URL}/api/config/legal`).then((r) => r.json());
      const newMeta = {};
      list.forEach((d) => { newMeta[d.slug] = d; });
      setMeta(newMeta);

      const fetched = await Promise.all(
        SLUGS.map(({ slug }) => fetch(`${API_URL}/api/config/legal/${slug}`).then((r) => r.json())),
      );
      const newDocs = {};
      fetched.forEach((d) => {
        // Don't pre-populate the editor with the placeholder default — show
        // empty so admin starts from scratch.
        newDocs[d.slug] = meta[d.slug]?.has_content ? d.html : (newMeta[d.slug]?.has_content ? d.html : '');
      });
      setDocs(newDocs);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const html = docs[active] || '';
      const r = await fetch(`${API_URL}/api/config/legal/${active}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ html }),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading legal documents…
      </div>
    );
  }

  const activeMeta = SLUGS.find((s) => s.slug === active);

  return (
    <div className="space-y-5" data-testid="legal-docs-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <FileText className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">Legal Documents (Rich Editor)</p>
          <p className="text-cyan-800">
            Edit the four legal documents customers must accept during registration.
            Each opens in a public page (e.g. <code>/terms</code>) — links live in the registration form checkboxes.
          </p>
        </div>
      </div>

      {/* Sub-tabs for the 4 documents */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {SLUGS.map((s) => (
          <button
            key={s.slug}
            type="button"
            onClick={() => { setActive(s.slug); setPreviewing(false); }}
            data-testid={`legal-tab-${s.slug}`}
            className={`px-4 py-2 text-sm font-medium relative ${
              active === s.slug
                ? 'text-cyan-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              {s.label}
              {meta[s.slug]?.has_content
                ? <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">live</span>
                : <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">empty</span>}
            </span>
            {active === s.slug && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600" />
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base font-bold text-slate-800">{activeMeta?.label}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{activeMeta?.hint}</p>
            <a
              href={`/${active === 'refund' ? 'refund-policy' : active}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-cyan-600 hover:underline mt-1 inline-block"
              data-testid={`legal-public-link-${active}`}
            >
              Open public page → /{active === 'refund' ? 'refund-policy' : active}
            </a>
          </div>
          <button
            type="button"
            onClick={() => setPreviewing((p) => !p)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
            data-testid="legal-toggle-preview"
          >
            {previewing ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {previewing ? 'Edit' : 'Preview'}
          </button>
        </div>

        {previewing ? (
          <SafeHtml as="div" className="border border-slate-200 rounded-lg bg-slate-50 p-4 min-h-[260px] legal-preview"
            data-testid="legal-preview" html={docs[active] || '<em style="color:#94a3b8">Empty — public page will show a placeholder.</em>'} />
        ) : (
          <div className="quill-wrapper border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500" data-testid={`legal-editor-${active}`}>
            <ReactQuill
              theme="snow"
              value={docs[active] || ''}
              onChange={(html) => setDocs((cur) => ({ ...cur, [active]: html }))}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Write the contents of this legal document…"
            />
          </div>
        )}

        <div className="flex gap-2 items-center mt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-legal-doc"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save {activeMeta?.label}
          </button>
          {savedFlash && (
            <span className="text-emerald-600 text-sm font-medium" data-testid="legal-saved">✓ Saved</span>
          )}
          {meta[active]?.updated_at && (
            <span className="text-xs text-slate-400 ml-auto">
              Last updated: {new Date(meta[active].updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <style>{`
        .quill-wrapper .ql-toolbar { border: 0; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
        .quill-wrapper .ql-container { border: 0; min-height: 240px; font-size: 14px; }
        .quill-wrapper .ql-editor { min-height: 240px; }
        .legal-preview h1 { font-size: 1.5rem; font-weight: 700; margin: 0.75rem 0; }
        .legal-preview h2 { font-size: 1.25rem; font-weight: 700; margin: 0.5rem 0; }
        .legal-preview p  { margin: 0.5rem 0; line-height: 1.6; }
        .legal-preview ul { list-style: disc; padding-left: 1.5rem; }
        .legal-preview ol { list-style: decimal; padding-left: 1.5rem; }
      `}</style>
    </div>
  );
}
